import { AfterViewInit, Component, ElementRef, input, model, QueryList, signal, ViewChildren } from '@angular/core';
import {
  SynButtonComponent,
  SynIconButtonComponent,
  SynIconComponent,
  SynInputComponent, SynValidateComponent,
} from '@synergy-design-system/angular';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors, Validator,
} from '@angular/forms';
import { SynInputEvent } from '@synergy-design-system/components';

@Component({
  selector: 'app-text-list',
  template: `
    @if (!disabled() || texts().length > 0) {
      <!-- eslint-disable-next-line @angular-eslint/template/label-has-associated-control -->
      <label>{{ label() }}</label>
    }
    <div class="flex-col gap-1" #list>
      @for (text of texts(); track $index) {
        <div class="flex-row gap-1" [attr.data-index]="$index">
          <syn-validate on="invalid change" style="flex-grow: 1">
            <syn-input
              [value]="text"
              [attr.data-index]="$index"
              (synInputEvent)="onInput($event)"
              [placeholder]="placeholder()"
              [disabled]="disabled()"
              [readonly]="readonly()"
              [required]="required()"
              [size]="size()"
              [pattern]="regexPattern()"
              #input />
          </syn-validate>
          <syn-icon-button
            name="delete_fill"
            label="Delete"
            i18n-label="@@delete"
            size="medium"
            [disabled]="disabled() || readonly()"
            (click)="onDeleteEntry($index)"
          />
        </div>
      }
      @if (!readonly()) {
        <div>
          <syn-button (click)="onAdd()">
            <syn-icon name="add_fill" slot="prefix" />
            <span>{{ addLabel() }}</span>
          </syn-button>
        </div>
      }
    </div>
  `
  ,
  styles: [
    `
      syn-input {
        width: 100%;
      }
    `,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: TextListComponent,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: TextListComponent,
      multi: true,
    },
  ],
  imports: [SynButtonComponent, SynIconButtonComponent, SynInputComponent, SynIconComponent, SynValidateComponent],
})
export class TextListComponent implements AfterViewInit, ControlValueAccessor, Validator {
  texts = model(['']);

  placeholder = input('');
  label = input('');
  readonly = input(false);
  disabled = model(false);
  required = input(false);
  size = input<'small' | 'medium' | 'large'>('medium');
  regexPattern = input('');

  addLabel = input('');

  added = signal(false);

  @ViewChildren('input') inputs!: QueryList<ElementRef<HTMLInputElement>>;

  ngAfterViewInit() {
    this.inputs.changes.subscribe(() => {
      // Do not focus for the initial load
      if (!this.added()) return;

      // Focus newly added syn-input
      const lastInput = this.inputs.last.nativeElement;
      // We have to wait until the synergy component is completely initialized
      // If we directly call focus() the internal input member will be null
      setTimeout(() => {
        lastInput.focus();
      });
    });
  }

  onInput(event: SynInputEvent) {
    const target = (event.target as HTMLInputElement);
    const indexString = target?.dataset['index'];
    if (!indexString) return;
    const index = parseInt(indexString);

    const newValue = target.value;
    this.texts.update((value) => {
      value[index] = newValue;
      this.onChange(value);
      return value;
    });
  }

  onDeleteEntry(index: number) {
    this.texts.update((value) => {
      value.splice(index, 1);
      this.onChange(value);
      return value;
    });
  }

  onAdd() {
    this.texts.update((value) => {
      value.push('');
      this.onChange(value);
      return value;
    });
    this.added.set(true);
  }

  // ControlValueAccessor implementation
  // This is required to be used in Angular forms
  writeValue(obj: string[]): void {
    this.texts.update(() => obj);
  }

  onChange: (value: string[]) => void = () => {
  };
  onTouched: () => void = () => {
  };

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const values: string[] | undefined = control.value;
    if (!values) return null;

    const exp = new RegExp(this.regexPattern());
    const valid = values.every((v) => exp.test(v));
    if (valid) return null;
    return { error: 'pattern-does-not-match' };
  }
}
